import { Box, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';

import { useLanguage } from '../i18n/LanguageContext';

import { formatMoney } from './format';

type Line = { description: string; quantity: number; unit: string; unitPriceMinor: number };

type InvoiceLinesProps = {
  lines: Array<Line>;
  netMinor: number;
  vatMinor: number;
  totalMinor: number;
  vatPayer: boolean;
  vatRateBasisPoints: number;
  currency: string;
};

/**
 * A document's lines and totals, in the document's own order. A document, not a list to search: it has no
 * filters or sorting (Conventions/08 does not apply — R0006 D-17).
 */
export const InvoiceLines = ({
  lines,
  netMinor,
  vatMinor,
  totalMinor,
  vatPayer,
  vatRateBasisPoints,
  currency,
}: InvoiceLinesProps) => {
  const { language, t } = useLanguage();
  const money = (minor: number): string => formatMoney(minor, currency, language);

  return (
    <Box>
      <Table size="small">
        <TableBody>
          <TableRow>
            <TableCell sx={{ color: 'text.secondary' }}>{t('invoice.line')}</TableCell>
            <TableCell align="right" sx={{ color: 'text.secondary' }}>
              {t('invoice.quantity')}
            </TableCell>
            <TableCell align="right" sx={{ color: 'text.secondary' }}>
              {t('invoice.unitPrice')}
            </TableCell>
            <TableCell align="right" sx={{ color: 'text.secondary' }}>
              {t('invoice.lineTotal')}
            </TableCell>
          </TableRow>
          {lines.map((line, index) => (
            <TableRow key={`${index}-${line.description}`}>
              <TableCell>{line.description}</TableCell>
              <TableCell align="right">
                {line.quantity} {line.unit}
              </TableCell>
              <TableCell align="right">{money(line.unitPriceMinor)}</TableCell>
              <TableCell align="right">{money(line.quantity * line.unitPriceMinor)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Stack spacing={0.5} sx={{ mt: 2, ml: 'auto', maxWidth: 320 }}>
        {vatPayer ? (
          <>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t('invoice.net')}
              </Typography>
              <Typography variant="body2">{money(netMinor)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t('invoice.vat', { rate: vatRateBasisPoints / 100 })}
              </Typography>
              <Typography variant="body2">{money(vatMinor)}</Typography>
            </Stack>
          </>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {t('invoice.noVat')}
          </Typography>
        )}
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="subtitle2">{t('invoice.total')}</Typography>
          <Typography variant="subtitle2">{money(totalMinor)}</Typography>
        </Stack>
      </Stack>
    </Box>
  );
};
